import React, { useState } from "react";
import { Offcanvas, Table, Form } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { ApiHook } from "../../hooks/apiHook";
import SubmitButton from "../Common/buttons/SubmitButton";
import { useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import CurrencyConverter from "../../Currency/CurrencyConverter";
import { reverseNumberDisplay } from "../../utils/currencyNumberDisplay";
import { toast } from "react-toastify";
import CurrencyInput from "react-currency-input-field";
import { Link } from "react-router-dom";

const MiWalletPayoutRequest = ({ show, handleClose, data }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [payoutData, setPayoutData] = useState({
    payoutAmount: "",
    transactionPassword: "",
  });
  const [errorMessage, setErrorMessage] = useState({
    payoutAmount: null,
    transactionPassword: null,
  });
  const userSelectedCurrency = useSelector(
    (state) => state?.user?.selectedCurrency
  );
  const conversionFactor = useSelector(
    (state) => state?.user?.conversionFactor
  );
  const defaultCurrencyJson = useSelector(
    (state) => state?.user?.loginResponse?.defaultCurrency
  );

  const defaultCurrency = defaultCurrencyJson
    ? JSON.parse(defaultCurrencyJson)
    : null;
  const payoutMutation = ApiHook.CallMiWalletPayoutRequest();

  const handleChange = (e) => {
    const { id, value } = e.target;
    setPayoutData((prevData) => ({
      ...prevData,
      [id]: value,
    }));
    setErrorMessage((prevData) => ({
      ...prevData,
      [id]: null,
    }));
    if (value === null || value === "") {
      setErrorMessage((prev) => ({
        ...prev,
        [id]: "*Required",
      }));
    }
  };

  const isFormValid = () => {
    return (
      payoutData?.payoutAmount > 0 &&
      payoutData?.transactionPassword.trim() !== ""
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isFormValid()) {
      return;
    }
    let convertAmount;
    if (userSelectedCurrency.id === defaultCurrency.id) {
      convertAmount = reverseNumberDisplay(
        CurrencyConverter(payoutData?.payoutAmount, conversionFactor, 0)
      );
    } else {
      convertAmount = reverseNumberDisplay(
        CurrencyConverter(payoutData?.payoutAmount, conversionFactor, 1)
      );
    }
    const postData = {
      payoutAmount: convertAmount,
      transactionPassword: payoutData?.transactionPassword,
    };
    await payoutMutation.mutateAsync(postData, {
      onSuccess: (res) => {
        if (res.status === 200) {
          setErrorMessage({
            username: null,
            transactionPassword: null,
          });
          setPayoutData({
            payoutAmount: "",
            transactionPassword: "",
          });
          queryClient.invalidateQueries({
            queryKey: ["payout-request-details"],
          });
          queryClient.invalidateQueries({
            queryKey: ["payout-details"],
          });
          queryClient.invalidateQueries({
            queryKey: ["payout-tiles"],
          });
          handleClose(false);
        } else {
          if (res?.data?.code === 1015) {
            setErrorMessage((prevErrors) => ({
              ...prevErrors,
              transactionPassword: t(res?.data?.description),
            }));
          } else if (res?.data?.code === 1027) {
            setErrorMessage((prevErrors) => ({
              ...prevErrors,
              payoutAmount: t(res?.data?.description),
            }));
          } else if (res?.data?.code === 1073) {
            setErrorMessage((prevErrors) => ({
              ...prevErrors,
              transactionPassword: t("addBankDetails"),
            }));
            toast.error(t(res?.data?.description));
          } else if (res?.data?.code === 1028) {
            setErrorMessage((prevErrors) => ({
              ...prevErrors,
              payoutAmount: t(res?.data?.description),
            }));
          } else if (res?.data?.code === 1025) {
            setErrorMessage((prevErrors) => ({
              ...prevErrors,
              payoutAmount: t(res?.data?.description),
            }));
          } else if (res?.data?.code) {
            toast.error(t(res?.data?.description));
          } else {
            toast.error(t(res?.data?.message));
          }
        }
      },
    });
  };

  return (
    <Offcanvas
      show={show}
      placement="end"
      onHide={() => {
        handleClose();
        setPayoutData({
          payoutAmount: "",
          transactionPassword: "",
        });
        setErrorMessage({
          username: null,
          transactionPassword: null,
        });
      }}
      scroll={true}
      backdrop={true}
    >
      <Offcanvas.Header closeButton>
        <Offcanvas.Title>{t("MiWalletPayoutRequest")}</Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body>
        <Form>
          <Form.Group className="mb-3 amount-field">
            {data?.isMiWalletPayoutMethodUpdated === false && (
              <Link to="/profile">{t("updatePayout")}</Link>
            )}
            <br />
            <Form.Label>{t("miwithdrawalAmount")}</Form.Label>
            <div className="input-group">
              <Form.Control as="select" disabled className="max-40">
                <option value="">{userSelectedCurrency?.symbolLeft}</option>
              </Form.Control>
              <CurrencyInput
                name="payoutAmount"
                id="payoutAmount"
                placeholder={t("coin")}
                value={payoutData?.payoutAmount}
                onValueChange={(value, id) =>
                  handleChange({ target: { value, id } })
                }
                required
                allowNegativeValue={false}
              />
              <div className="number-field-invalid-feedback">
                {errorMessage.payoutAmount}
              </div>
            </div>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>{t("transactionPassword")} *</Form.Label>
            <Form.Control
              id="transactionPassword"
              type="password"
              placeholder={t("transactionPassword")}
              onChange={(e) => handleChange(e)}
              value={payoutData?.transactionPassword}
              required
              isInvalid={errorMessage?.transactionPassword !== null}
            />
            <Form.Control.Feedback type="invalid">
              {errorMessage?.transactionPassword && (
                <Link to="/profile" style={{ color: "red" }}>
                  {errorMessage.transactionPassword}
                </Link>
              )}
            </Form.Control.Feedback>
          </Form.Group>
          <SubmitButton
            isSubmitting={
              !isFormValid() ||
              payoutMutation.isLoading ||
              data?.isMiWalletPayoutMethodUpdated === false
            }
            click={handleSubmit}
            text={payoutMutation.isLoading ? "Submitting..." : "Submit"}
            className={"btn btn-primary submit_btn"}
          />
        </Form>
        {data?.payoutFee !== 0 && (
          <p>
            {t("an_additional_amount_of")}
            <span className="popAmntVal">
              {data?.payoutFeeMode === "percentage"
                ? `${data?.payoutFee} %`
                : `${userSelectedCurrency?.symbolLeft} ${CurrencyConverter(
                    Number(data?.payoutFee),
                    conversionFactor
                  )}`}
            </span>
            {t("will_be_debited_as_payout_fee")}
          </p>
        )}
        <Table className="table payout_pop_table">
          <thead>
            <tr>
              <th colSpan="2">{t("particulars")}</th>
            </tr>
          </thead>
          <tbody>
            {data &&
              Object.entries(data).map(
                ([key, value]) =>
                  key !== "payoutMethod" &&
                  key !== "isPayoutMethodUpdated" &&
                  key !== "isMiWalletPayoutMethodUpdated" &&
                  key !== "ewalletBalance" &&
                  key !== "availablePayoutAmount" &&
                  key !== "payoutFee" &&
                  key !== "payoutFeeMode" &&
                  key !== "defaultCurrency" &&
                  key !== "availablePayoutAmount" &&
                  key !== "minPayoutAmount" &&
                  key !== "maxPayoutAmount" &&
                  key !== "availablePayoutAmount" &&
                  key !== "requestInProgress" &&
                  key !== "totalPaid" &&key !== "isUserPayoutEnabled"&&
                  (key !== key) !== "isUserPayoutEnabled" && (
                    <tr key={key}>
                      <td>{t(`${key}`)}</td>
                      {/* {key === "defaultCurrency" && (
                        <td>{`(${userSelectedCurrency?.symbolLeft})`}</td>
                      )} */}
                      {key === "payoutMethod" && <td>{value}</td>}
                      {key === "MiWalletPayoutMethod" && <td>{value}</td>}

                      {/* {key === "requestValidity" && (
                  <td>{`${value} ${t("days")}`}</td>
                )} */}
                      {key === "ewalletBalance" && (
                        <td>{`${
                          userSelectedCurrency?.symbolLeft
                        } ${CurrencyConverter(
                          Number(value),
                          conversionFactor
                        )}`}</td>
                      )}
                      {key === "miCoinWalletBalance" && (
                        <td>{Number(value)}</td>
                      )}
                      {key === "MiMaxPayoutAmount" && <td>{Number(value)}</td>}
                      {key === "MiMinPayoutAmount" && <td>{Number(value)}</td>}
                      {key === "miPayoutFee" && (
                        <td>
                          {data.payoutFeeMode === "percentage" ? (
                            <span className="popAmntVal">{`${data.payoutFee} %`}</span>
                          ) : (
                            Number(data.payoutFee)
                          )}
                        </td>
                      )}
                      {key === "mirequestInProgress" && (
                        <td>{Number(value)}</td>
                      )}
                      {key === "mitotalPaid" && <td>{Number(value)}</td>}
                      {/* {key === "availablePayoutAmount" && (
                        <td>{Number(value)}</td>
                      )} */}
                      {key === "availableMiWalletPayoutAmount" && (
                        <td>{Number(value)}</td>
                      )}

                      {key === "miPayoutFeeMode" && <td>{t(value)}</td>}
                    </tr>
                  )
              )}
          </tbody>
        </Table>
      </Offcanvas.Body>
    </Offcanvas>
  );
};

export default MiWalletPayoutRequest;
